<script setup lang="ts">
import { ChevronDownIcon } from '@gohighlevel/ghl-icons/24/outline'
import type { Component, PropType } from 'vue'
interface SelectOption {
  label: string
  value: string
}

const emit = defineEmits(['change'])

const _props = defineProps({
  options: {
    type: Array as PropType<SelectOption[]>,
    required: true,
  },
  modelValue: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: 'Select an option',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  icon: {
    type: [Object, Function] as PropType<Component>,
    default: () => null,
  },
  label: {
    type: String,
    default: '',
  },
  id: {
    type: String,
    default: () => `select-${Math.random().toString(36).substr(2, 9)}`,
  },
})

const getOptionLabel = (option: SelectOption): string => {
  return option.label
}

const getOptionValue = (option: SelectOption): string => {
  return option.value
}

const handleChange = (event: Event) => {
  const target = event.target as HTMLSelectElement
  emit('change', target.value)
}
</script>

<template>
  <div class="relative w-full">
    <div
      class="relative flex h-[35px] w-full items-center rounded-full border border-solid border-[#5f6368] bg-transparent backdrop-blur-sm transition-all duration-200"
    >
      <div
        v-if="icon"
        class="community-select-icon pointer-events-none absolute left-3 z-10 h-4 w-4 text-[#a8c7fa]"
      >
        <component :is="icon" />
      </div>
      <select
        :id="id"
        :value="modelValue"
        :disabled="disabled"
        :aria-label="label || placeholder"
        class="h-[35px] w-full cursor-pointer appearance-none rounded-full border-none bg-transparent px-4 pr-8 text-[13px] leading-[18px] text-white outline-none transition-all duration-200"
        :class="{ 'pl-9': icon }"
        @change="handleChange"
      >
        <option
          value=""
          disabled
          class="bg-gray-800 px-3 py-2 text-[13px] leading-[18px] text-gray-400"
        >
          {{ placeholder }}
        </option>
        <option
          v-for="option in options"
          :key="getOptionValue(option)"
          :value="getOptionValue(option)"
          class="bg-gray-700 px-3 py-2 text-[13px] leading-[18px] text-white hover:bg-gray-600"
        >
          {{ getOptionLabel(option) }}
        </option>
      </select>
      <div
        class="community-select-arrow-icon pointer-events-none absolute right-3 h-4 w-4 text-white transition-transform duration-200"
      >
        <ChevronDownIcon class="h-4 w-4" />
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Custom scrollbar for dropdown */
select::-webkit-scrollbar {
  width: 6px;
}

select::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.3);
  border-radius: 3px;
}

select::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

select::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.5);
}
</style>
